import React, { useCallback, useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import moment from "moment";
import axios from ".././axios-interceptor";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import NewInvDetailedView from "./NewInvDetailedView";
import {
  base_url,
  STATUS_CLOSED,
  OPPORTUNITY_STATUS_OPEN,
  OPPORTUNITY_STATUS_FULLY_FUNDED,
  OPPORTUNITY_STATUS_DISBURSING,
  OPPORTUNITY_STATUS_PROCESSED,
  formatMoney,
} from "./../utilities";
import {
  MultiInvestorDisburseModal,
  MultiInvestorPayTenureModal,
  MultiInvestorWithdrawalReviewModal,
} from "./MultiInvestorModals";

// Fund Now (multi-investor) loans as rows of the admin "New Investment Interests" table, in the
// same 10-column layout as the legacy interest rows. One row per loan; its investors and any
// pending withdrawal requests expand underneath. Disbursement and payout use the per-investor
// split modals; loan details open the shared NewInvDetailedView.

// Brand colours come from the BenneFin palette tokens in App.css :root.
const C = {
  blue: "var(--ben-blue)",
  navy: "var(--ben-navy)",
  sesame: "var(--ben-sesame)",
  sesameText: "var(--ben-sesame-dark)",
  green: "#059669",
  orange: "#FF9B06",
  gray: "var(--ben-grey-dark)",
  graytext: "#7C8495",
  border: "var(--ben-track)",
  amber: "#854F0B",
  amberbg: "#FAEEDA",
  amberborder: "#FAC775",
  red: "#ED5151",
};

export const COLUMN_COUNT = 10;

const statusLabel = (row) => {
  if (row.loan_status === STATUS_CLOSED) return { text: "Closed", color: C.gray };
  switch (row.status) {
    case OPPORTUNITY_STATUS_OPEN:
      return { text: "Open for funding", color: C.blue };
    case OPPORTUNITY_STATUS_FULLY_FUNDED:
      return { text: "Fully funded", color: C.navy };
    case OPPORTUNITY_STATUS_DISBURSING:
      return { text: "Disbursing", color: C.sesameText };
    case OPPORTUNITY_STATUS_PROCESSED:
      return { text: "Payment Processed", color: C.blue };
    default:
      return { text: "-", color: C.graytext };
  }
};

const ProgressBar = ({ pct }) => {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ height: 6, background: C.border, borderRadius: 999, overflow: "hidden", minWidth: 90, marginTop: 4 }}>
      <div style={{ width: p + "%", height: "100%", background: p >= 100 ? C.navy : C.blue, borderRadius: 999 }} />
    </div>
  );
};

const Badge = ({ children, title }) => (
  <span
    title={title}
    style={{
      background: C.amberbg,
      color: C.amber,
      border: `1px solid ${C.amberborder}`,
      borderRadius: 12,
      fontSize: 11.5,
      padding: "1px 8px",
      fontWeight: 600,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const linkStyle = { fontSize: 12, fontWeight: 700, color: C.blue, whiteSpace: "nowrap", cursor: "pointer" };

const SUB_STATE = {
  pledged: { text: "Pledged", color: C.blue },
  disbursed: { text: "Disbursed", color: C.navy },
  closed: { text: "Closed", color: C.graytext },
  withdrawn: { text: "Withdrawn", color: C.red },
};

const matchesSearch = (o, q) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return [o.loan_reference_number, o.business_name].some((v) => String(v || "").toLowerCase().includes(needle));
};

const FundNowLoanRows = ({ canWrite, search = "", ltwo, onLoaded, focusRequestId = null }) => {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());
  // investor_request_id -> { loading, investors }
  const [breakdowns, setBreakdowns] = useState({});

  const [disburseRow, setDisburseRow] = useState(null);
  const [tenureRow, setTenureRow] = useState(null);
  const [review, setReview] = useState({ row: null, mode: "approve" });
  const [details, setDetails] = useState(null);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const loadBreakdown = useCallback((investorRequestId) => {
    const key = String(investorRequestId);
    setBreakdowns((b) => ({ ...b, [key]: { loading: true, investors: b[key]?.investors || [] } }));
    axios
      .post(base_url + "loan-investor-subscriptions/get_breakdown", { investor_request_id: investorRequestId })
      .then((res) => {
        const investors = res?.data?.status === 200 ? res.data.data?.investors || [] : [];
        setBreakdowns((b) => ({ ...b, [key]: { loading: false, investors } }));
      })
      .catch(() => setBreakdowns((b) => ({ ...b, [key]: { loading: false, investors: [] } })));
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.post(base_url + "loan-investor-subscriptions/get_admin_opportunities").catch(() => null),
      axios.post(base_url + "loan-investor-subscriptions/get_pending_withdrawals").catch(() => null),
    ]).then(([oppRes, pendRes]) => {
      const nextOpps = oppRes?.data?.status === 200 ? oppRes.data.data || [] : [];
      const nextPending = pendRes?.data?.status === 200 ? pendRes.data.data || [] : [];
      setOpps(nextOpps);
      setPending(nextPending);
      setLoading(false);
      if (onLoadedRef.current) onLoadedRef.current(nextOpps, nextPending);
      // Loans with a withdrawal waiting for review open by default, so the request is visible.
      setExpanded((prev) => {
        const next = new Set(prev);
        nextOpps.forEach((o) => {
          if (Number(o.pending_withdrawals) > 0) next.add(String(o.investor_request_id));
        });
        return next;
      });
      setBreakdowns({});
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (focusRequestId) setExpanded((prev) => new Set(prev).add(String(focusRequestId)));
  }, [focusRequestId]);

  // Fetch investors for every expanded row that has not been loaded since the last refresh.
  useEffect(() => {
    expanded.forEach((id) => {
      if (!breakdowns[id]) loadBreakdown(id);
    });
  }, [expanded, breakdowns, loadBreakdown]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const canDisburse = (o) =>
    o.loan_status !== STATUS_CLOSED &&
    (o.status === OPPORTUNITY_STATUS_FULLY_FUNDED || o.status === OPPORTUNITY_STATUS_DISBURSING);
  // Only when a repaid, checker-verified tenure is waiting (QA #18B: it opened an empty modal).
  // D7: Admin may close funding once the loan is at least 80% funded (and not yet full).
  const canCloseFunding = (o) =>
    o.status === OPPORTUNITY_STATUS_OPEN && Number(o.percent_funded) >= 80 && Number(o.percent_funded) < 100 &&
    Number(o.pending_withdrawals || 0) === 0 && !o.edit_in_progress;
  const closeFunding = async (o) => {
    const confirm = await Swal.fire({
      title: `Close funding at ${Number(o.percent_funded)}%?`,
      html: `The loan amount becomes <b>${formatMoney(o.funded, o.currency)}</b> (from ${formatMoney(o.target_amount, o.currency)}) and the repayment schedule is recalculated. The business and all investors are notified.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Close funding",
    });
    if (!confirm.isConfirmed) return;
    const res = await axios.post(base_url + "loan-investor-subscriptions/close_funding", { investor_request_id: o.investor_request_id });
    if (res?.data?.status === 200) {
      toast.success(res.data.message, { toastId: 520 });
      refresh();
    }
  };
  const canPayTenure = (o) =>
    o.status === OPPORTUNITY_STATUS_PROCESSED && o.loan_status !== STATUS_CLOSED && Number(o.payable_tenure_count) > 0;

  if (loading || ltwo) {
    return [0, 1].map((i) => (
      <tr key={"fn-sk" + i} className="new_inv_interst_table">
        {Array.from({ length: COLUMN_COUNT }).map((_, j) => (
          <td key={j}>
            <Skeleton width={90} height={15} />
          </td>
        ))}
      </tr>
    ));
  }

  const visible = opps.filter((o) => matchesSearch(o, search));

  const renderInvestors = (o) => {
    const key = String(o.investor_request_id);
    const b = breakdowns[key];
    // D1: approvable until disbursement starts (open or fully funded).
    const approveLocked = (o.status !== OPPORTUNITY_STATUS_OPEN && o.status !== OPPORTUNITY_STATUS_FULLY_FUNDED) || !!o.funding_closed_early;
    const lockedText = o.funding_closed_early
      ? "Funding was closed early - withdrawal locked, reject only"
      : "Disbursement has started - withdrawal locked, reject only";
    return (
      <tr key={key + "-inv"}>
        <td colSpan={COLUMN_COUNT} style={{ background: "#F8FAFF", padding: "8px 24px 12px" }}>
          {!b || b.loading ? (
            <Skeleton height={18} count={2} />
          ) : b.investors.length === 0 ? (
            <div style={{ fontSize: 12.5, color: C.graytext, textAlign: "left" }}>No investors have funded this loan yet.</div>
          ) : (
            <table className="w-100" style={{ fontSize: 12.5 }}>
              <thead>
                <tr style={{ color: C.graytext }}>
                  <th style={{ textAlign: "left", fontWeight: 600 }}>Investor</th>
                  <th style={{ fontWeight: 600 }}>Amount</th>
                  <th style={{ fontWeight: 600 }}>Share</th>
                  <th style={{ fontWeight: 600 }}>State</th>
                  <th style={{ fontWeight: 600 }}>Withdrawal</th>
                </tr>
              </thead>
              <tbody>
                {b.investors.map((inv) => {
                  const s = SUB_STATE[inv.state] || SUB_STATE.pledged;
                  const w = pending.find((p) => String(p.subscription_id) === String(inv.subscription_id));
                  return (
                    <tr key={String(inv.subscription_id)} style={{ borderTop: `1px solid ${C.border}` }}>
                      <td style={{ textAlign: "left", padding: "6px 0" }}>
                        {inv.investor_name || "-"}
                        {inv.investor_reference_no ? (
                          <span style={{ color: C.graytext }}> · {inv.investor_reference_no}</span>
                        ) : null}
                      </td>
                      <td>{formatMoney(inv.subscribed_amount, o.currency)}</td>
                      <td>{inv.state === "withdrawn" ? "-" : `${Number(inv.share_percent || 0)}%`}</td>
                      <td style={{ color: s.color, fontWeight: 600 }}>{s.text}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {!w ? (
                          <span style={{ color: C.graytext }}>-</span>
                        ) : (
                          <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                            <span style={{ color: C.amber, fontWeight: 600 }}>
                              Requested {w.withdrawal_requested_date_time ? moment(w.withdrawal_requested_date_time).format("D MMM") : ""}
                            </span>
                            {w.withdrawal_reason ? (
                              <span title={w.withdrawal_reason} style={{ color: C.graytext, maxWidth: 180 }} className="text-truncate">
                                "{w.withdrawal_reason}"
                              </span>
                            ) : null}
                            {canWrite && (
                              <>
                                <span title={approveLocked ? lockedText : undefined}>
                                  <button
                                    className="btn px-2 py-0"
                                    style={{ backgroundColor: C.green, color: "white", fontSize: 12, pointerEvents: approveLocked ? "none" : undefined }}
                                    disabled={!!review.row || approveLocked}
                                    aria-label={approveLocked ? lockedText : "Approve withdrawal"}
                                    onClick={() => setReview({ row: w, mode: "approve" })}
                                  >
                                    Approve
                                  </button>
                                </span>
                                <button
                                  className="btn px-2 py-0"
                                  style={{ backgroundColor: C.red, color: "white", fontSize: 12 }}
                                  disabled={!!review.row}
                                  onClick={() => setReview({ row: w, mode: "reject" })}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </td>
      </tr>
    );
  };

  return (
    <>
      {visible.map((o) => {
        const key = String(o.investor_request_id);
        const isOpen = expanded.has(key);
        const s = statusLabel(o);
        const warnText = [
          !o.counter_in_sync ? "Funded counter does not match the pledge rows (reconcile needed)" : "",
          o.edit_in_progress ? "An investor breakdown edit is in progress" : "",
        ]
          .filter(Boolean)
          .join(". ");
        return (
          <React.Fragment key={key}>
            <tr className="new_inv_interst_table">
              <td>
                <span
                  className="Cpointer"
                  role="button"
                  aria-expanded={isOpen}
                  onClick={() => toggle(key)}
                  style={{ fontWeight: 600, color: C.blue, whiteSpace: "nowrap" }}
                >
                  {isOpen ? "▾" : "▸"} {Number(o.investor_count) === 1 ? "1 investor" : `${Number(o.investor_count) || 0} investors`}
                </span>
                {Number(o.pending_withdrawals) > 0 && (
                  <div className="mt-1">
                    <Badge title="Pending withdrawal requests">{o.pending_withdrawals} withdrawal pending</Badge>
                  </div>
                )}
              </td>
              <td>{o.business_name || "-"}</td>
              <td style={{ whiteSpace: "nowrap" }}>
                {o.loan_reference_number || "-"}
                {warnText && (
                  <span title={warnText} aria-label={warnText} style={{ color: C.orange, marginLeft: 6, fontWeight: 800, cursor: "help" }}>
                    &#9888;
                  </span>
                )}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {formatMoney(o.funded, o.currency)}
                <div style={{ fontSize: 11.5, color: C.graytext }}>
                  of {formatMoney(o.target_amount, o.currency)} · <b>{Number(o.percent_funded || 0)}%</b>
                </div>
                <ProgressBar pct={o.percent_funded} />
              </td>
              <td>{o.bennefin_roi === undefined || o.bennefin_roi === null ? "-" : o.bennefin_roi + "%"}</td>
              <td>{o.investor_roi === undefined || o.investor_roi === null ? "-" : o.investor_roi + "%"}</td>
              <td>-</td>
              <td>
                <span className="newinvintstatus" style={{ color: s.color, fontWeight: 600 }}>
                  {s.text}
                  <br />
                  <span style={{ fontSize: 11, color: C.graytext, fontWeight: 500 }}>
                    {o.funding_closed_early ? `Closed at ${o.funding_closed_early.percent}%` : "Fund Now"}
                  </span>
                </span>
              </td>
              <td>
                {o.tenure_months === undefined ? "-" : o.tenure_months + (Number(o.tenure_months) === 1 ? " Month" : " Months")}
              </td>
              <td>
                <div className="allinve_action_td">
                  {canWrite && canCloseFunding(o) && (
                    <span className="pe-3" style={linkStyle} title="Close funding at the current percentage" onClick={() => closeFunding(o)}>
                      Close funding
                    </span>
                  )}
                  {canWrite && canDisburse(o) && (
                    <img
                      className="pe-3 Cpointer"
                      src="./images/check.svg"
                      alt="Disburse to business"
                      title={o.status === OPPORTUNITY_STATUS_DISBURSING ? "Resume disbursement" : "Disburse to business"}
                      onClick={() => !disburseRow && setDisburseRow(o)}
                    />
                  )}
                  {canWrite && canPayTenure(o) && (
                    <span className="pe-3" style={linkStyle} title="Pay a tenure to all investors" onClick={() => !tenureRow && setTenureRow(o)}>
                      Pay investors
                    </span>
                  )}
                  <img
                    className="indiv_loan_dtls_eye"
                    src="./images/icons/blue_eye.svg"
                    alt="Loan details"
                    title="Loan details"
                    onClick={() => setDetails({ loan_id: o.loan_id, status: o.status })}
                  />
                </div>
              </td>
            </tr>
            {isOpen && renderInvestors(o)}
          </React.Fragment>
        );
      })}

      <MultiInvestorDisburseModal show={!!disburseRow} row={disburseRow} canWrite={canWrite} onHide={() => setDisburseRow(null)} onDone={refresh} />
      <MultiInvestorPayTenureModal show={!!tenureRow} row={tenureRow} canWrite={canWrite} onHide={() => setTenureRow(null)} onDone={refresh} />
      <MultiInvestorWithdrawalReviewModal
        show={!!review.row}
        row={review.row}
        mode={review.mode}
        canWrite={canWrite}
        onHide={() => setReview({ row: null, mode: "approve" })}
        onDone={refresh}
      />
      {details && (
        <NewInvDetailedView
          data={{ loan_id: details.loan_id }}
          opportunityStatus={details.status}
          show={!!details}
          onHide={() => {
            setDetails(null);
            // Payments, closure etc. inside the detailed view change these rows' status columns.
            refresh();
          }}
        />
      )}
    </>
  );
};

export default FundNowLoanRows;
